import { Link } from 'react-router-dom';
import SeoHead from '../../components/feature/SeoHead';
import Footer from '../home/components/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const packagesSchema = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Lotus Maternal Doula Packages — Brooklyn, NY',
  description: 'Holistic doula care packages for all families in Brooklyn, NY ranging from $600 to $5,000.',
  url: `${siteUrl}/packages`,
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Packages', item: `${siteUrl}/packages` },
    ],
  },
  itemListElement: [
    {
      '@type': 'ListItem', position: 1,
      item: {
        '@type': 'Offer', name: 'Princess Package', price: '600', priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        seller: { '@type': 'MedicalBusiness', name: 'Lotus Maternal' },
      },
    },
    {
      '@type': 'ListItem', position: 2,
      item: {
        '@type': 'Offer', name: 'Queen Package', price: '800', priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        seller: { '@type': 'MedicalBusiness', name: 'Lotus Maternal' },
      },
    },
    {
      '@type': 'ListItem', position: 3,
      item: {
        '@type': 'Offer', name: 'Empress Package', price: '1000', priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        seller: { '@type': 'MedicalBusiness', name: 'Lotus Maternal' },
      },
    },
    {
      '@type': 'ListItem', position: 4,
      item: {
        '@type': 'Offer', name: 'Goddess Package', price: '1200', priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        seller: { '@type': 'MedicalBusiness', name: 'Lotus Maternal' },
      },
    },
    {
      '@type': 'ListItem', position: 5,
      item: {
        '@type': 'Offer', name: 'Prenatal Package', price: '2500', priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        seller: { '@type': 'MedicalBusiness', name: 'Lotus Maternal' },
      },
    },
    {
      '@type': 'ListItem', position: 6,
      item: {
        '@type': 'Offer', name: 'Postpartum Package', price: '2000', priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        seller: { '@type': 'MedicalBusiness', name: 'Lotus Maternal' },
      },
    },
    {
      '@type': 'ListItem', position: 7,
      item: {
        '@type': 'Offer', name: 'Full Journey Package', price: '5000', priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        seller: { '@type': 'MedicalBusiness', name: 'Lotus Maternal' },
      },
    },
  ],
};

const mainPackages = [
  {
    name: 'Princess',
    price: '$600',
    priceValue: 600,
    icon: 'ri-seedling-line',
    gradient: 'from-pink-50 to-rose-50',
    accent: 'text-rose-500',
    border: 'border-rose-200',
    badge: null,
    features: [
      '1 Prenatal & Postpartum Visit',
      '1 OB/Midwife Visit',
      'Nutritionist Meal Plan (prenatal & postpartum)',
      'Birth Planning Session',
      'Herb & Tincture Recommendations',
    ],
  },
  {
    name: 'Queen',
    price: '$800',
    priceValue: 800,
    icon: 'ri-vip-crown-line',
    gradient: 'from-amber-50 to-orange-50',
    accent: 'text-amber-700',
    border: 'border-amber-300',
    badge: 'POPULAR',
    features: [
      '2 Prenatal & Postpartum Visits',
      '2 OB/Midwife Visits',
      'Protective Hair Style',
      '24/7 Text Support During On Call Period',
      'Birth Planning Session',
      'Herb & Tincture Recommendations',
    ],
  },
  {
    name: 'Empress',
    price: '$1,000',
    priceValue: 1000,
    icon: 'ri-star-line',
    gradient: 'from-teal-50 to-green-50',
    accent: 'text-teal-600',
    border: 'border-teal-300',
    badge: 'POPULAR',
    features: [
      '3 Prenatal & Postpartum Visits',
      '3 OB/Midwife Visits',
      'Protective Hair Style',
      'Prenatal Yoga Class',
      '24/7 Call/Text Support',
      'Birth Planning Session',
    ],
  },
  {
    name: 'Goddess',
    price: '$1,200',
    priceValue: 1200,
    icon: 'ri-sparkling-line',
    gradient: 'from-fuchsia-50 to-pink-50',
    accent: 'text-fuchsia-600',
    border: 'border-fuchsia-200',
    badge: 'PREMIUM',
    features: [
      '4 Prenatal & Postpartum Visits',
      '4 OB/Midwife Visits',
      'Placenta Encapsulation or Lotus Birth',
      'Nutritionist Meal Prep',
      'Prenatal & Postnatal Herbs & Teas',
      'Protective Hair Style',
      'Prenatal Yoga Class',
      '24/7 Call/Text Support',
      'Nursery Space Set Up',
      'Prenatal & Postnatal Massages',
      'Hospital Bag Prep',
      'Perineal Bath & Yoni Steam',
    ],
  },
];

interface Service {
  name: string;
  price: string;
  priceValue: number | string;
  icon: string;
  description: string;
  category: string;
}

const alaCarteServices: Service[] = [
  {
    name: 'Birth Plan',
    price: '$20',
    priceValue: 20,
    icon: 'ri-file-list-3-line',
    description: 'Personalized birth preferences document crafted with your doula to communicate your wishes to your care team.',
    category: 'Planning',
  },
  {
    name: 'Perineal Bath',
    price: '$20',
    priceValue: 20,
    icon: 'ri-drop-line',
    description: 'Soothing herbal perineal bath to support healing and comfort in the postpartum period.',
    category: 'Postpartum Care',
  },
  {
    name: 'Birth Ball Rental',
    price: '$20',
    priceValue: 20,
    icon: 'ri-circle-line',
    description: 'Rental of a professional birth ball to support labor positioning, comfort, and prenatal exercise.',
    category: 'Equipment',
  },
  {
    name: 'Prenatal Yoga with Birth Ball Session',
    price: '$30',
    priceValue: 30,
    icon: 'ri-heart-pulse-line',
    description: 'Guided prenatal yoga session incorporating birth ball techniques to prepare your body and mind for labor.',
    category: 'Wellness',
  },
  {
    name: 'Live Birth Record',
    price: '$50',
    priceValue: 50,
    icon: 'ri-vidicon-line',
    description: 'Professional documentation of your birth story through photography or video to treasure forever.',
    category: 'Documentation',
  },
  {
    name: 'Lactation Support',
    price: '$50',
    priceValue: 50,
    icon: 'ri-heart-line',
    description: 'One-on-one lactation guidance to help establish a comfortable and successful breastfeeding journey.',
    category: 'Postpartum Care',
  },
  {
    name: 'Individual Virtual Support (60 mins)',
    price: '$50',
    priceValue: 50,
    icon: 'ri-vidicon-2-line',
    description: 'A dedicated 60-minute virtual session for personalized support, questions, and guidance from your doula.',
    category: 'Virtual',
  },
  {
    name: 'Prenatal or Postnatal Massage (60 mins)',
    price: '$100',
    priceValue: 100,
    icon: 'ri-hand-heart-line',
    description: 'Therapeutic 60-minute massage tailored to your prenatal or postnatal needs for relaxation and recovery.',
    category: 'Wellness',
  },
  {
    name: 'Lotus Birth',
    price: '$100',
    priceValue: 100,
    icon: 'ri-seedling-line',
    description: 'Full support for a lotus birth experience, honoring the sacred connection between baby and placenta.',
    category: 'Birth Services',
  },
  {
    name: 'Protective Hair Style',
    price: '$100–$300',
    priceValue: 100,
    icon: 'ri-scissors-line',
    description: 'Beautiful protective styling to keep you feeling confident and comfortable throughout your journey.',
    category: 'Beauty & Wellness',
  },
  {
    name: 'Rights Over Your Birth Package',
    price: '$250',
    priceValue: 250,
    icon: 'ri-shield-star-line',
    description: 'Education and advocacy support to help you understand and assert your rights during pregnancy and birth.',
    category: 'Education',
  },
  {
    name: 'La Bassine Maxi Birth Pool Rental',
    price: '$300',
    priceValue: 300,
    icon: 'ri-water-flash-line',
    description: 'Rental of the La Bassine Maxi inflatable birth pool for a peaceful, supported water birth experience.',
    category: 'Equipment',
  },
  {
    name: 'Labor + Birth Attendance Only',
    price: '$300',
    priceValue: 300,
    icon: 'ri-user-heart-line',
    description: 'Dedicated doula presence and continuous support throughout your active labor and birth.',
    category: 'Birth Services',
  },
  {
    name: 'Virtual Support Pre + Post',
    price: '$300',
    priceValue: 300,
    icon: 'ri-computer-line',
    description: 'Comprehensive virtual doula support covering both your prenatal preparation and postpartum recovery.',
    category: 'Virtual',
  },
  {
    name: 'Live-In Postpartum Support',
    price: '$600',
    priceValue: 600,
    icon: 'ri-home-heart-line',
    description: 'Immersive live-in postpartum care providing round-the-clock support for you and your newborn at home.',
    category: 'Postpartum Care',
  },
  {
    name: 'Labor + Birth Attendance (Hourly)',
    price: '$25/hr',
    priceValue: 25,
    icon: 'ri-time-line',
    description: 'Flexible hourly doula attendance during labor and birth for families who prefer pay-as-you-go support.',
    category: 'Birth Services',
  },
];

const categoryColors: Record<string, string> = {
  Planning: 'bg-amber-100 text-amber-700 border-amber-200',
  'Postpartum Care': 'bg-pink-100 text-pink-700 border-pink-200',
  Equipment: 'bg-teal-100 text-teal-700 border-teal-200',
  Wellness: 'bg-green-100 text-green-700 border-green-200',
  Documentation: 'bg-rose-100 text-rose-700 border-rose-200',
  Virtual: 'bg-sky-100 text-sky-700 border-sky-200',
  'Birth Services': 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200',
  'Beauty & Wellness': 'bg-orange-100 text-orange-700 border-orange-200',
  Education: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

const iconColors: Record<string, string> = {
  Planning: 'text-amber-600',
  'Postpartum Care': 'text-pink-600',
  Equipment: 'text-teal-600',
  Wellness: 'text-green-600',
  Documentation: 'text-rose-600',
  Virtual: 'text-sky-600',
  'Birth Services': 'text-fuchsia-600',
  'Beauty & Wellness': 'text-orange-600',
  Education: 'text-emerald-600',
};

export default function PackagesPage() {
  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="Doula Packages Brooklyn NY — Princess to Full Journey | Lotus Maternal"
        description="Lotus Maternal offers holistic doula packages in Brooklyn, NY starting at $600. Princess, Queen, Empress, Goddess, Prenatal, Postpartum, and Full Journey packages — plus à la carte services."
        keywords="doula packages Brooklyn NY, birth doula pricing, postpartum doula package, prenatal support package, holistic maternal care New York"
        canonical={`${siteUrl}/packages`}
        ogTitle="Doula Packages — Princess to Full Journey | Lotus Maternal"
        ogDescription="Holistic doula care packages starting at $600. Choose the perfect level of support for your pregnancy, birth, and postpartum journey."
        schema={packagesSchema}
      />

      {/* ── Hero ── */}
      <div className="relative h-[420px] overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/15f12a73c4befeade4459f6bf54f13bb.jpg"
          alt="Lotus Maternal Doula Packages"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-white" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full px-6 py-2.5 mb-5">
            <i className="ri-gift-2-line text-amber-300 text-sm" />
            <span className="text-white text-xs font-semibold tracking-widest uppercase">Lotus Maternal · Brooklyn, NY</span>
          </div>
          <h1 className="font-display text-6xl lg:text-7xl font-bold text-white drop-shadow-2xl mb-4 tracking-tight">
            Our Care Packages
          </h1>
          <p className="text-white/90 text-lg max-w-xl drop-shadow-md">
            Comprehensive holistic doula support designed for every stage of your journey
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-20">

        {/* ── Free Manual Banner ── */}
        <div className="bg-gradient-to-r from-amber-100 via-amber-50 to-green-50 rounded-3xl p-8 mb-14 border-2 border-amber-200 shadow-md flex flex-col md:flex-row items-center gap-8">
          <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-white shadow-md flex-shrink-0">
            <i className="ri-book-open-line text-5xl text-amber-700" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-display text-2xl font-bold text-neutral-800 mb-2">
              Free Birth Plan Included
            </h3>
            <p className="text-neutral-700 text-sm leading-relaxed mb-3">
              Every package includes our comprehensive <strong>Birth Plan + Customized Chart</strong> — a $40 value, absolutely free. Covers birth planning, postpartum recovery, newborn care, and personalized resources.
            </p>
            <Link to="/manual" className="inline-flex items-center gap-1.5 text-amber-700 font-bold text-sm hover:text-amber-800 transition-colors cursor-pointer whitespace-nowrap">
              Learn More About the Birth Plan <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>

        {/* ── All Packages Include ── */}
        <div className="bg-gradient-to-r from-pink-50/70 to-teal-50/70 rounded-2xl px-10 py-6 mb-16 text-center border border-pink-200/60">
          <p className="text-sm text-neutral-800 leading-relaxed">
            <strong className="text-amber-700">All packages include:</strong>{' '}
            Birth planning, attending labor, massage at visit, nutritionist meal plan (not prep), birthing ball workout,
            photography/videography (if you wish), herb &amp; tincture recommendations, minimal lactation support, and perineal bath.
          </p>
        </div>

        {/* ── 4 Main Package Cards ── */}
        <h2 className="font-display text-4xl font-bold text-neutral-800 text-center mb-3">Wombman Packages</h2>
        <p className="text-neutral-500 text-center text-sm mb-10">Choose the level of support that feels right for you</p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {mainPackages.map((pkg) => (
            <div
              key={pkg.name}
              className={`relative bg-gradient-to-b ${pkg.gradient} rounded-3xl p-8 border-2 ${pkg.border} hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 flex flex-col`}
            >
              {pkg.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-700 text-white text-xs font-bold px-5 py-1.5 rounded-full whitespace-nowrap shadow-md">
                  {pkg.badge}
                </div>
              )}
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white shadow-md mb-5">
                <i className={`${pkg.icon} text-2xl ${pkg.accent}`} />
              </div>
              <h3 className={`font-display text-2xl font-bold mb-0.5 ${pkg.accent}`}>{pkg.name}</h3>
              <p className="text-xs text-neutral-400 mb-4">Package</p>
              <div className="font-display text-5xl font-bold text-neutral-800 mb-7">{pkg.price}</div>
              <ul className="space-y-2.5 mb-8 flex-1">
                {pkg.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-neutral-700">
                    <i className="ri-checkbox-circle-fill text-teal-500 text-base mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={`/checkout?item=${pkg.name} Package&price=${pkg.priceValue}&type=package`}
                className="w-full py-3.5 bg-amber-700 text-white font-bold rounded-full hover:bg-amber-800 hover:shadow-lg transition-all whitespace-nowrap cursor-pointer text-sm text-center block"
              >
                Purchase Now
              </Link>
            </div>
          ))}
        </div>

        {/* ── Prenatal Package ── */}
        <h2 className="font-display text-4xl font-bold text-neutral-800 text-center mb-3">Extended Packages</h2>
        <p className="text-neutral-500 text-center text-sm mb-10">Full-spectrum care for every phase of your journey</p>

        <div className="relative rounded-3xl overflow-hidden mb-10 shadow-xl">
          <img
            src="https://readdy.ai/api/search-image?query=African%20American%20pregnant%20woman%20with%20natural%20hair%20doing%20prenatal%20yoga%20in%20a%20bright%20airy%20minimalist%20studio%20peaceful%20serene%20expression%20warm%20golden%20afternoon%20light%20rich%20deep%20melanin%20skin%20tones%20authentic%20Black%20wellness%20pregnancy%20fitness%20empowered%20motherhood%20soft%20bokeh%20background%20wide%20cinematic%20shot&width=1920&height=600&seq=prenatal-pkg-aa-v2-003&orientation=landscape"
            alt="Prenatal Package — Full support from 1st Trimester through birth"
            className="w-full h-80 object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-transparent flex items-center px-14">
            <div className="max-w-xl">
              <span className="inline-block bg-amber-500/90 text-white text-xs font-bold px-4 py-1.5 rounded-full mb-4 tracking-widest uppercase">Prenatal</span>
              <h3 className="font-display text-5xl font-bold text-white mb-2">Prenatal Package</h3>
              <div className="font-display text-6xl font-bold text-amber-400 mb-4">$2,500</div>
              <p className="text-white/85 text-sm mb-6 leading-relaxed">
                Full support from 1st Trimester through birth — weekly check-ins, OB visits, meal preps, birth plan, prenatal massages, lactation support &amp; more.
              </p>
              <Link
                to="/checkout?item=Prenatal Package&price=2500&type=package"
                className="inline-flex items-center gap-2 px-10 py-3.5 bg-white text-amber-700 font-bold rounded-full hover:shadow-2xl hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-shopping-cart-line" />
                Purchase Now
              </Link>
            </div>
          </div>
        </div>

        {/* ── Postpartum + Full Journey ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {/* Postpartum */}
          <div className="relative rounded-3xl overflow-hidden shadow-xl">
            <img
              src="https://readdy.ai/api/search-image?query=African%20American%20mother%20with%20natural%20hair%20gently%20cradling%20newborn%20baby%20in%20a%20cozy%20warm%20sunlit%20bedroom%20soft%20natural%20window%20light%20peaceful%20postpartum%20recovery%20scene%20rich%20deep%20melanin%20skin%20tones%20authentic%20Black%20motherhood%20tender%20intimate%20serene%20home%20setting%20minimal%20decor&width=900&height=700&seq=postpartum-pkg-aa-v2-003&orientation=portrait"
              alt="Postpartum Package"
              className="w-full h-72 object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent flex flex-col justify-end p-10">
              <span className="inline-block bg-rose-500/80 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit tracking-widest uppercase">Postpartum</span>
              <h3 className="font-display text-4xl font-bold text-white mb-1">Postpartum Package</h3>
              <div className="font-display text-5xl font-bold text-amber-400 mb-3">$2,000</div>
              <p className="text-white/80 text-sm mb-5">2 Postpartum visits + everything in wombman packages</p>
              <Link
                to="/checkout?item=Postpartum Package&price=2000&type=package"
                className="inline-flex items-center gap-2 px-8 py-3 bg-amber-700 text-white font-bold rounded-full hover:bg-amber-800 transition-all whitespace-nowrap cursor-pointer text-sm w-fit"
              >
                <i className="ri-shopping-cart-line" />
                Purchase Now
              </Link>
            </div>
          </div>

          {/* Full Journey */}
          <div className="relative rounded-3xl overflow-hidden shadow-xl">
            <img
              src="https://readdy.ai/api/search-image?query=joyful%20African%20American%20family%20mother%20father%20and%20newborn%20baby%20together%20in%20a%20bright%20sunlit%20modern%20home%20warm%20golden%20hour%20light%20beautiful%20rich%20deep%20melanin%20skin%20tones%20authentic%20Black%20family%20portrait%20loving%20embrace%20celebrating%20new%20life%20full%20journey%20parenthood%20minimal%20clean%20interior&width=900&height=700&seq=fulljourney-pkg-aa-v2-003&orientation=portrait"
              alt="Full Journey Package"
              className="w-full h-72 object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent flex flex-col justify-end p-10">
              <span className="inline-block bg-teal-500/80 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit tracking-widest uppercase">Full Journey</span>
              <h3 className="font-display text-4xl font-bold text-white mb-1">Full Journey Package</h3>
              <div className="font-display text-5xl font-bold text-teal-400 mb-3">$5,000</div>
              <p className="text-white/80 text-sm mb-5">Complete support from pregnancy through postpartum — our most comprehensive care</p>
              <Link
                to="/checkout?item=Full Journey Package&price=5000&type=package"
                className="inline-flex items-center gap-2 px-8 py-3 bg-teal-600 text-white font-bold rounded-full hover:bg-teal-700 transition-all whitespace-nowrap cursor-pointer text-sm w-fit"
              >
                <i className="ri-shopping-cart-line" />
                Purchase Now
              </Link>
            </div>
          </div>
        </div>

        {/* ── À La Carte Section ── */}
        <div className="mb-20">
          {/* Section Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-full px-5 py-2 mb-4">
              <i className="ri-list-check-2 text-teal-600 text-sm" />
              <span className="text-teal-700 text-xs font-semibold tracking-widest uppercase">Build Your Own</span>
            </div>
            <h2 className="font-display text-4xl font-bold text-neutral-800 mb-3">À La Carte Services</h2>
            <p className="text-neutral-500 text-sm max-w-2xl mx-auto leading-relaxed">
              Not every family needs a full package. Pick exactly the services that matter most to you — mix and match to create the perfect support for your journey. Starting at just $20.
            </p>
          </div>

          {/* Category Legend */}
          <div className="flex flex-wrap gap-2 mb-8 justify-center">
            {Array.from(new Set(alaCarteServices.map((s) => s.category))).map((cat) => (
              <span
                key={cat}
                className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold border ${categoryColors[cat] || 'bg-neutral-100 text-neutral-600 border-neutral-200'}`}
              >
                {cat}
              </span>
            ))}
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {alaCarteServices.map((service) => (
              <div
                key={service.name}
                className="bg-white rounded-2xl border border-neutral-100 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col overflow-hidden"
              >
                <div className="p-5 flex-1">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-neutral-50 border border-neutral-100">
                      <i className={`${service.icon} text-xl ${iconColors[service.category] || 'text-neutral-600'}`} />
                    </div>
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${categoryColors[service.category] || 'bg-neutral-100 text-neutral-600 border-neutral-200'}`}
                    >
                      {service.category}
                    </span>
                  </div>
                  <h3 className="font-display text-base font-bold text-neutral-800 mb-1.5 leading-snug">
                    {service.name}
                  </h3>
                  <p className="text-neutral-500 text-xs leading-relaxed">
                    {service.description}
                  </p>
                </div>
                <div className="px-5 pb-5 flex items-center justify-between gap-3">
                  <span className="font-display text-xl font-bold text-amber-700">{service.price}</span>
                  <Link
                    to={`/checkout?item=${encodeURIComponent(service.name)}&price=${service.priceValue}&type=alacarte`}
                    className="flex-shrink-0 px-4 py-2 bg-pink-600 text-white font-bold rounded-full hover:bg-pink-700 transition-all whitespace-nowrap cursor-pointer text-xs"
                  >
                    Book Now
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Free Consultation CTA ── */}
        <div className="relative rounded-3xl overflow-hidden shadow-xl">
          <img
            src="https://readdy.ai/api/search-image?query=warm%20inviting%20consultation%20room%20with%20soft%20natural%20light%20comfortable%20chairs%20lush%20green%20plants%20neutral%20beige%20tones%20minimal%20elegant%20interior%20design%20peaceful%20calm%20atmosphere%20professional%20wellness%20space%20wide%20shot%20African%20American%20woman%20doula%20smiling&width=1920&height=500&seq=consult-cta-aa-v2-002&orientation=landscape"
            alt="Book a Free Consultation"
            className="w-full h-64 object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-transparent flex items-center px-14">
            <div className="max-w-xl">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-amber-100 mb-4">
                <i className="ri-calendar-check-line text-amber-700 text-xl" />
              </div>
              <h3 className="font-display text-4xl font-bold text-white mb-2">Not Sure Which Package Is Right for You?</h3>
              <p className="text-white/85 text-sm mb-6 leading-relaxed">
                Book a free 30-minute consultation and we'll help you find the perfect fit for your journey. No pressure, just a warm conversation.
              </p>
              <a
                href="https://calendly.com/queenbriebirthworker/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-10 py-4 bg-amber-700 text-white font-bold rounded-full hover:bg-amber-800 hover:shadow-xl transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calendar-line text-lg" />
                Book Free Consultation
              </a>
            </div>
          </div>
        </div>

      </div>

      <Footer />
    </div>
  );
}